import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, CheckCircle2, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function CoverageAreasPage() {
  const boroughs = [
    {
      name: 'Lambeth',
      zone: 'Central',
      description: 'Comprehensive coverage across all Lambeth neighborhoods'
    },
    {
      name: 'Wandsworth',
      zone: 'South West',
      description: 'Full service availability throughout Wandsworth borough'
    },
    {
      name: 'Westminster',
      zone: 'Central',
      description: 'Premium services in the heart of London'
    },
    {
      name: 'City of London',
      zone: 'Central',
      description: 'Commercial and residential plumbing in the Square Mile'
    },
    {
      name: 'Fulham',
      zone: 'West',
      description: 'Expert plumbing services across Fulham and surrounding areas'
    },
    {
      name: 'Brent',
      zone: 'North West',
      description: 'Reliable service throughout Brent borough'
    },
    {
      name: 'Bromley',
      zone: 'South East',
      description: 'Complete coverage of Bromley and nearby areas'
    },
    {
      name: 'Camden',
      zone: 'North',
      description: 'Professional plumbing across Camden neighborhoods'
    },
    {
      name: 'Greenwich',
      zone: 'South East',
      description: '24/7 emergency service in Greenwich borough'
    },
    {
      name: 'Hackney',
      zone: 'East',
      description: 'Fast response times throughout Hackney'
    },
    {
      name: 'Hammersmith',
      zone: 'West',
      description: 'Complete plumbing solutions for Hammersmith residents'
    },
    {
      name: 'Islington',
      zone: 'North',
      description: 'Trusted service across Islington and Angel'
    }
  ];

  const zones = [
    { name: 'Central London', count: 3, color: 'bg-primary' },
    { name: 'North London', count: 2, color: 'bg-accent' },
    { name: 'South London', count: 3, color: 'bg-primary' },
    { name: 'East London', count: 2, color: 'bg-accent' },
    { name: 'West London', count: 2, color: 'bg-primary' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-b from-muted/30 to-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6 mb-16 animate-slide-up">
            <Badge className="bg-primary/10 text-primary border-primary/20">
              Service Coverage
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight">
              Serving All of{' '}
              <span className="text-primary">Greater London</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Elite Plumbers proudly serves residential and commercial clients across 
              all major London boroughs. With over a decade of experience working with 
              local councils, we understand the unique needs of London properties.
            </p>
          </div>

          {/* Zone Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-16">
            {zones.map((zone, index) => (
              <Card key={index} className="p-4 text-center border-2 hover:border-primary/50 transition-all duration-300">
                <div className={`w-3 h-3 rounded-full ${zone.color} mx-auto mb-2`} />
                <p className="font-semibold text-foreground text-sm">{zone.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{zone.count} boroughs</p>
              </Card>
            ))}
          </div>

          {/* Interactive Map Section */}
          <Card className="p-8 md:p-12 border-2 shadow-lg">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Map Placeholder */}
              <div className="relative h-[400px] lg:h-[500px] bg-muted/30 rounded-2xl overflow-hidden group">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center space-y-4 p-8">
                    <MapPin className="w-16 h-16 text-primary mx-auto" />
                    <div>
                      <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
                        London Coverage Map
                      </h3>
                      <p className="text-muted-foreground">
                        We serve all major boroughs across Greater London
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 justify-center mt-6">
                      {['Central', 'North', 'South', 'East', 'West'].map((direction) => (
                        <Badge key={direction} variant="outline" className="border-primary/30 text-primary">
                          {direction} London
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Decorative elements */}
                <div className="absolute top-10 left-10 w-4 h-4 rounded-full bg-primary animate-pulse" />
                <div className="absolute top-20 right-20 w-3 h-3 rounded-full bg-primary/70 animate-pulse" style={{ animationDelay: '0.5s' }} />
                <div className="absolute bottom-20 left-20 w-3 h-3 rounded-full bg-primary/70 animate-pulse" style={{ animationDelay: '1s' }} />
                <div className="absolute bottom-10 right-10 w-4 h-4 rounded-full bg-primary animate-pulse" style={{ animationDelay: '1.5s' }} />
              </div>

              {/* Info */}
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
                    Comprehensive Coverage Across London
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Our strategically located teams ensure professional service to any 
                    location in London. With over 20 years of experience and a decade of 
                    partnership with London Boroughs, we\'re never more than a call away.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">Borough Approved Services</p>
                      <p className="text-sm text-muted-foreground">Working with local councils for over 10 years</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">Safety First Approach</p>
                      <p className="text-sm text-muted-foreground">All work meets highest safety standards</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">Local Knowledge</p>
                      <p className="text-sm text-muted-foreground">Expert understanding of London properties and regulations</p>
                    </div>
                  </div>
                </div>

                <Button 
                  asChild
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-glow w-full sm:w-auto"
                >
                  <a href="tel:07356255155" className="flex items-center justify-center gap-2">
                    <Phone className="w-5 h-5" />
                    Call 07356 255155
                  </a>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Boroughs List Section */}
      <section className="py-20 md:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground">
              Coverage by Borough
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Elite Plumbers provides comprehensive plumbing services across these London boroughs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {boroughs.map((borough, index) => (
              <Card 
                key={index}
                className="p-6 border-2 hover:border-primary/50 hover:shadow-lg transition-all duration-300 group cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300 flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-heading font-semibold text-foreground group-hover:text-primary transition-colors duration-300">
                        {borough.name}
                      </h3>
                      <Badge variant="outline" className="text-xs border-muted-foreground/30">
                        {borough.zone}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {borough.description}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Card className="inline-block p-6 border-2 border-primary/20 bg-primary/5">
              <p className="text-sm text-muted-foreground mb-4">
                Don\'t see your area listed? We may still be able to help!
              </p>
              <Button 
                asChild
                variant="outline"
                className="border-2 hover:bg-muted"
              >
                <Link to="/contact">Contact Us to Check</Link>
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* London Areas Showcase */}
      <section className="py-20 bg-muted/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground">
              Serving London's Finest Homes
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From historic properties to modern developments, we bring expertise to every job
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="overflow-hidden border-2 group">
              <div className="relative h-64">
                <img
                  src="https://images.unsplash.com/photo-1584622650111-993a426fbf0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxiYXRocm9vbSUyMHJlbm92YXRpb258ZW58MHx8fHwxNzY5ODE5NzkxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Central London property"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <Badge className="bg-primary/90 text-primary-foreground mb-2">
                    Central London
                  </Badge>
                  <h3 className="text-lg font-heading font-semibold text-white">
                    Premium Installations
                  </h3>
                </div>
              </div>
            </Card>

            <Card className="overflow-hidden border-2 group">
              <div className="relative h-64">
                <img
                  src="https://images.unsplash.com/photo-1629079447777-1e605162dc8d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxiYXRocm9vbSUyMHJlbm92YXRpb258ZW58MHx8fHwxNzY5ODE5NzkxfDA&ixlib=rb-4.1.0&q=85"
                  alt="North London property"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <Badge className="bg-primary/90 text-primary-foreground mb-2">
                    North London
                  </Badge>
                  <h3 className="text-lg font-heading font-semibold text-white">
                    Modern Renovations
                  </h3>
                </div>
              </div>
            </Card>

            <Card className="overflow-hidden border-2 group">
              <div className="relative h-64">
                <img
                  src="https://images.unsplash.com/photo-1521783593447-5702b9bfd267?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxiYXRocm9vbSUyMHJlbm92YXRpb258ZW58MHx8fHwxNzY5ODE5NzkxfDA&ixlib=rb-4.1.0&q=85"
                  alt="South London property"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <Badge className="bg-primary/90 text-primary-foreground mb-2">
                    South London
                  </Badge>
                  <h3 className="text-lg font-heading font-semibold text-white">
                    Complete Solutions
                  </h3>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-secondary text-secondary-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold">
            Need Professional Plumbing in Your Area?
          </h2>
          <p className="text-lg text-secondary-foreground/80">
            Get in touch today for professional plumbing services anywhere in London. 
            Trusted by residents and boroughs for over 20 years.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-glow"
            >
              <Link to="/contact">Request a Quote</Link>
            </Button>
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-secondary-foreground/20 hover:bg-secondary-foreground/10"
            >
              <a href="tel:07356255155">Contact Us Today</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}